import Grid from '@precis-digital/kurama/Grid'
import { styled } from '@precis-digital/kurama/utils'
import Typography from '@precis-digital/kurama/Typography'
import {
  getSafeErrorCode,
  isPipelineLabelErrorCode,
  isTargetGcpProjectErrorCode,
  STATUSES,
  type ConfigStatus,
  type ErrorCodeType,
  type ErrorDetails
} from 'shared/configStatuses'
import { APP_ROUTES } from 'shared/routes'
import { useTranslation } from 'shared/translations'
import { usePathname } from 'shared/hooks/useRouterPath'
interface StatusTooltipBodyProps {
  errorCode?: ErrorCodeType | null
  status: ConfigStatus
  hasLinkToDagView: boolean
  errorDetails: ErrorDetails | null | string
  /** Non-error explanatory copy (e.g. paused / pending) — not for failure details. */
  statusMessage?: string | null
  openDagView?: () => void
  shouldShowStatusMessageInBody?: boolean
}

const StatusTooltipBody: React.FC<StatusTooltipBodyProps> = ({
  status,
  hasLinkToDagView = false,
  errorCode,
  errorDetails,
  statusMessage,
  openDagView,
  shouldShowStatusMessageInBody = true
}): React.ReactElement => {
  const { t } = useTranslation('common')
  const getMessage = (): string => {
    switch (status) {
      case STATUSES.error: {
        if (typeof errorDetails === 'string' && errorDetails !== '') {
          return errorDetails
        }

        const code = getSafeErrorCode(errorCode)
        const placeholders = typeof errorDetails === 'object' ? errorDetails?.translationPlaceholders : undefined

        if (placeholders == null || Object.keys(placeholders).length === 0) {
          return t(`errors.${code}`)
        }

        if (isPipelineLabelErrorCode(code) && placeholders.pipelineLabel != null) {
          return t(`errors.${code}`, { pipelineLabel: placeholders.pipelineLabel })
        }

        if (isTargetGcpProjectErrorCode(code) && placeholders.targetGCPProjectID != null) {
          return t(`errors.${code}`, { targetGCPProjectID: placeholders.targetGCPProjectID })
        }

        if (code === 'ERR201' && placeholders.errorMessage != null) {
          return t('errors.ERR201', { errorMessage: placeholders.errorMessage })
        }

        return t(`errors.${code}`)
      }
      case STATUSES.disconnected:
        return t('errors.ERR100')
      case STATUSES.disabled:
        return typeof errorDetails === 'string' ? errorDetails : ''
      case STATUSES.running:
        return t('configIsRunning')
      case STATUSES.paused:
        return statusMessage != null && statusMessage !== '' ? statusMessage : t('dagView.pausedConfigMessage')
      default:
        return ''
    }
  }

  const { pathname } = usePathname()
  const isBudgetOptimiserRoute = pathname.includes(APP_ROUTES.budgetOptimiser.basePage)
  const STATUS_BODY_MAP: Partial<Record<ConfigStatus, React.ReactElement>> = {
    [STATUSES.paused]: <Typography variant="body2">{getMessage()}</Typography>,
    [STATUSES.error]: (
      <Grid sx={{ cursor: hasLinkToDagView ? 'pointer' : 'default' }} onClick={openDagView}>
        <TextContainer $hasLinkToDagView={hasLinkToDagView}>
          <ErrorTypography $status={status} color="errorInverse">
            <strong>{t('weFoundAnError')}</strong>
            <span>{getMessage()}</span>
          </ErrorTypography>
        </TextContainer>
        <Grid>{hasLinkToDagView && <Typography variant="body2">{t('clickToViewDetails')}</Typography>}</Grid>
      </Grid>
    ),
    [STATUSES.disconnected]: (
      <TextContainer $hasLinkToDagView={hasLinkToDagView}>
        <ErrorTypography $status={status} variant="body2" color="errorInverse">
          <strong>{t('weFoundAnError')}</strong>
          <span>{getMessage()}</span>{' '}
        </ErrorTypography>
      </TextContainer>
    ),
    [STATUSES.running]: (
      <Grid sx={{ cursor: hasLinkToDagView ? 'pointer' : 'default' }} onClick={openDagView}>
        <TextContainer $hasLinkToDagView={hasLinkToDagView}>
          <Typography variant="body2">
            <strong>{t('currentlyRunning')}</strong>
          </Typography>
          <Typography variant="body2">{getMessage()}</Typography>
        </TextContainer>
        {hasLinkToDagView && (
          <Grid>
            <Typography variant="body2">{t('clickToViewDetails')}</Typography>
          </Grid>
        )}
      </Grid>
    ),
    [STATUSES.active]: (
      <Grid sx={{ cursor: hasLinkToDagView ? 'pointer' : 'default' }} onClick={openDagView}>
        {shouldShowStatusMessageInBody && <Typography variant="body2">{t('everythingIsRunningSmoothly')}</Typography>}
        {hasLinkToDagView && (
          <Grid>
            <Typography variant="body2">{t('clickToViewDetails')}</Typography>
          </Grid>
        )}
      </Grid>
    ),
    [STATUSES.connected]: shouldShowStatusMessageInBody ? (
      <Typography variant="body2">{t('everythingIsRunningSmoothly')}</Typography>
    ) : (
      <></>
    ),
    [STATUSES.pending]:
      statusMessage != null && statusMessage !== '' ? (
        <Typography variant="body2">{statusMessage}</Typography>
      ) : (
        <TextContainer $hasLinkToDagView={hasLinkToDagView}>
          <Typography variant="body2">
            <strong>{t('pending')}</strong>
          </Typography>
          <Typography variant="body2">
            {isBudgetOptimiserRoute
              ? t('dagView.pendingBudgetOptimiserConfigMessage')
              : t('dagView.noStatusDataAvailable')}
          </Typography>
        </TextContainer>
      ),
    [STATUSES.disabled]: <Typography variant="body2">{getMessage()}</Typography>
  }

  const tooltipBody = STATUS_BODY_MAP[status]

  return tooltipBody != null ? <Grid width="100%">{tooltipBody}</Grid> : <></>
}

const ErrorTypography = styled(Typography)<{ $status?: ConfigStatus }>(({ theme, $status: status }) => ({
  fontSize: '14px',
  display: '-webkit-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitLineClamp: 6,
  WebkitBoxOrient: 'vertical',
  span: {
    display: 'block'
  }
}))

const TextContainer = styled(Grid)<{ $hasLinkToDagView: boolean }>(
  ({ theme, $hasLinkToDagView: hasLinkToDagView }) => ({
    backgroundColor: `${theme.tokens.background.main.default}0D`,
    padding: '12px',
    borderRadius: theme.borderRadius.small,
    marginBottom: hasLinkToDagView ? '16px' : '0'
  })
)

export default StatusTooltipBody
